import { useSearchParams } from 'react-router-dom';
import { apiURL, useData, useSession } from '@yingbaba/api';
import { ActionDialog, Button, Notice, PageTitle, QueryState, Status } from '@yingbaba/ui';
import type { CollectionMethod } from '@yingbaba/types';

export function PaymentSettings() {
  const [params, setParams] = useSearchParams();
  const business = params.get('business') === 'digital' ? 'digital' : 'member';
  const role = useSession().data?.account.platform_role || '';
  const write = role === 'super_admin';
  const q = useData<CollectionMethod[]>(
    '/admin/collection-methods',
    write || ['finance', 'auditor'].includes(role),
  );
  const methods = (q.data || []).filter((row) => row.business === business);
  const businessName = business === 'member' ? '会员' : '数字资源';
  return (
    <>
      <PageTitle
        title="支付方式"
        action={
          <Button variant="secondary" onClick={() => void q.refetch()}>
            刷新检查
          </Button>
        }
        description="管理付费会员与数字资源的微信、支付宝普通商户收款。商户密钥和证书由服务器保管。"
      />
      <div className="tabs" aria-label="收款业务">
        {[
          ['member', '付费会员'],
          ['digital', '数字资源'],
        ].map(([value, label]) => (
          <button
            key={value}
            className={business === value ? 'active' : ''}
            aria-pressed={business === value}
            onClick={() => setParams({ business: value })}
          >
            {label}
          </button>
        ))}
      </div>
      <QueryState query={q}>
        {methods.length > 0 && !methods[0].server_enabled && (
          <Notice>
            {businessName}
            在线收款尚未启用。配置商户资料并完成接入验收后，开启服务器业务开关；这里只控制各场景是否允许使用。
          </Notice>
        )}
        <div className="collection-provider-grid">
          {[
            ['wechat', '微信支付'],
            ['alipay', '支付宝'],
          ].map(([provider, label]) => {
            const rows = methods.filter((row) => row.provider === provider),
              configured = rows.some((row) => row.merchant_configured);
            return (
              <section className="panel collection-provider" key={provider}>
                <div className="panel-title">
                  <h2>{label}</h2>
                  <Status
                    value={configured ? 'active' : 'pending'}
                    label={configured ? '商户资料已加载' : '商户资料待配置'}
                  />
                </div>
                <div className="collection-channel-list">
                  {rows.map((row) => (
                    <article key={row.channel} className="collection-channel">
                      <div className="row spread wrap">
                        <strong>{row.name}</strong>
                        <Status
                          value={row.available ? 'active' : 'disabled'}
                          label={row.available ? '可用' : !row.enabled ? '已暂停' : '未开放'}
                        />
                      </div>
                      <p className="small muted">
                        {row.available
                          ? '已满足本地配置条件，真实收款仍以渠道开通及验收为准。'
                          : row.reason}
                      </p>
                      <p className="small muted">
                        场景设置：{row.enabled ? '允许使用' : '暂停使用'}
                        {!row.server_allowed ? ' · 尚未加入服务器允许场景' : ''}
                      </p>
                      {write && (
                        <ActionDialog
                          title={`${row.enabled ? '暂停' : '允许'}${row.name}`}
                          trigger={row.enabled ? '暂停场景' : '允许场景'}
                          variant="secondary"
                          path={`/admin/collection-methods/${business}/${row.provider}/${row.channel}`}
                          method="PUT"
                          submitLabel={row.enabled ? '确认暂停' : '保存场景设置'}
                          description={
                            row.enabled
                              ? '暂停后不再创建新的渠道支付。已发出的支付仍会继续核验，到账不会遗漏。'
                              : '允许场景不会跳过服务器开关、商户配置或渠道资质检查。'
                          }
                          fields={[
                            {
                              name: 'reason',
                              label: '配置说明',
                              min: 4,
                              max: 500,
                              placeholder: '说明本次调整的原因',
                            },
                          ]}
                          transform={(v) => ({
                            enabled: !row.enabled,
                            expected_revision: row.revision,
                            reason: v.reason,
                          })}
                        />
                      )}
                    </article>
                  ))}
                </div>
                {rows[0] && (
                  <details className="collection-config">
                    <summary>接入配置与回调地址</summary>
                    <div className="stack small">
                      <p>
                        配置前缀：
                        <code>
                          {rows[0].config_prefix}
                          {provider === 'wechat' ? 'WECHAT_' : 'ALIPAY_'}
                        </code>
                        。付款商户为平台自己的普通商户，可为两项业务复用同一套证书文件，回调入口分别配置。
                      </p>
                      <div>
                        <strong>支付通知</strong>
                        <code className="collection-config-url">
                          {apiURL(rows[0].callback_path)}
                        </code>
                      </div>
                      {provider === 'alipay' && (
                        <p>
                          支付完成后返回 Work 的 <code>{rows[0].return_path}</code>{' '}
                          页面；返回页面只恢复查询，不作为到账凭证。
                        </p>
                      )}
                      <p>
                        业务开关：<code>{rows[0].config_prefix}PAY_ENABLED</code>；允许场景：
                        <code>{rows[0].config_prefix}PAY_CHANNELS</code>。API 与 Worker
                        使用相同商户配置。
                      </p>
                    </div>
                  </details>
                )}
              </section>
            );
          })}
        </div>
        {!write && <p className="muted small">当前为只读查看，支付场景由超级管理员调整。</p>}
      </QueryState>
    </>
  );
}
